import { useState, useEffect, useRef, useCallback } from "react";
import { notification } from "antd";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { store } from "../../../store";

/**
 * Custom hook that subscribes to gandalf's admin STOMP topic for real-time
 * research events. Replaces the previous 30-second polling approach.
 *
 * Events received: RESEARCHED, ACTIVITY (RAG_INGESTION, DELIVERY, etc.)
 *
 * Returns:
 *   onEvent — register a callback that fires on any research event
 *   connected — whether the STOMP connection is active
 */
export default function useResearchNotifications({ onEvent } = {}) {
  const [connected, setConnected] = useState(false);
  const clientRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const { loginReducer } = store.getState();
    const customerUuid = loginReducer?.groups?.[0];
    if (!customerUuid) return;

    // Connect to gandalf's admin STOMP endpoint
    const gandalfUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:80";
    // Strip /gandalfproxy if present — we need the raw gandalf URL
    const baseUrl = gandalfUrl.replace(/\/gandalfproxy$/, "");
    const wsUrl = baseUrl.replace(/^http/, "ws") + "/ws/admin";
    const sockJsUrl = baseUrl + "/ws/admin";

    const client = new Client({
      webSocketFactory: () => new SockJS(sockJsUrl),
      reconnectDelay: 5000,
      debug: (str) => {
        if (str.includes("CONNECTED") || str.includes("ERROR")) {
          console.debug("[STOMP Admin]", str);
        }
      },
    });

    client.onConnect = () => {
      setConnected(true);
      console.info("[STOMP Admin] Connected, subscribing to research events for", customerUuid);

      client.subscribe(`/topic/admin/${customerUuid}/research`, (message) => {
        try {
          const event = JSON.parse(message.body);
          console.debug("[STOMP Admin] Event:", event);

          // Fire the callback so the page can refresh
          if (onEventRef.current) {
            onEventRef.current(event);
          }

          // Show toast for key events
          if (event.eventType === "RESEARCHED") {
            notification.info({
              message: "Research Complete",
              description: `Request #${event.requestId} is ready for review.`,
              placement: "topRight",
              duration: 6,
            });
          }
        } catch (e) {
          console.warn("[STOMP Admin] Failed to parse event:", e);
        }
      });
    };

    client.onDisconnect = () => {
      setConnected(false);
    };

    client.onStompError = (frame) => {
      console.error("[STOMP Admin] Error:", frame.headers?.message);
      setConnected(false);
    };

    client.activate();
    clientRef.current = client;

    return () => {
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
    };
  }, []);

  return { connected };
}
